import { DeleteOutline } from "@mui/icons-material";
import {Box, Popover, Stack, Typography } from "@mui/material";
import React, {useState} from "react";
import Button from "@mui/material/Button";

export function PopoverButton({
    nodeId,
    nodeName,
    onRemove,
    theme,
    message,
    onConfirm,
    onCancel,
    trigger,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    buttonLabel = 'Remove',
    loading = false,
    popoverZIndex,
}: {
    nodeId?: number;
    nodeName?: string;
    onRemove?: (id: number) => void;
    theme?: any;
    /** Custom confirmation message (overrides default delete message). */
    message?: string;
    /** Custom confirm handler (used instead of onRemove when provided). */
    onConfirm?: () => void | Promise<void>;
    /** Optional handler when the cancel/No button is clicked (popover still closes). */
    onCancel?: () => void;
    /** Custom trigger element; when omitted, renders default Remove button. */
    trigger?: React.ReactElement;
    confirmLabel?: string;
    cancelLabel?: string;
    buttonLabel?: string;
    loading?: boolean;
    /** Raise popover above nested modals/drawers when needed. */
    popoverZIndex?: number;
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleCancelClick = () => {
        setAnchorEl(null);
        onCancel?.();
    };
    const handleConfirm = async () => {
        if (onConfirm) {
            await onConfirm();
        } else if (onRemove != null && nodeId != null) {
            onRemove(nodeId);
        }
        setAnchorEl(null);
    };

    const confirmMessage = message
        ?? (nodeName != null ? `Confirm to delete “${nodeName}”?` : 'Please confirm?');

    const defaultTrigger = (
        <Button size="small" variant="outlined"
                sx={{
                    minWidth: 80,
                    textTransform: 'none',
                    borderColor: theme?.palette.error.main,
                    color: theme?.palette.error.main,
                    fontWeight: 600,
                    '&:hover': {borderColor: theme?.palette.error.dark, color: theme?.palette.error.dark}
                }}
                startIcon={<DeleteOutline/>}
                onClick={handleClick}>
            {buttonLabel}
        </Button>
    );

    const triggerElement = trigger
        ? React.cloneElement(trigger, {
            onClick: (e: React.MouseEvent<HTMLElement>) => {
                trigger.props.onClick?.(e);
                handleClick(e);
            },
        })
        : defaultTrigger;

    return (
        <>
            {triggerElement}
            <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}
                     anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                     sx={popoverZIndex != null ? {zIndex: popoverZIndex} : undefined}>
                <Box p={2} sx={{maxWidth: 360}}>
                    <Typography variant="body2" sx={{mb: 1}}>{confirmMessage}</Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={handleCancelClick} disabled={loading}>{cancelLabel}</Button>
                        <Button size="small" color="error" variant="contained" onClick={handleConfirm} disabled={loading}>
                            {loading ? 'Processing...' : confirmLabel}
                        </Button>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
}
